<template>
  <ListItem
    :variant="appearance.variant"
    :leading-variant="appearance.leadingVariant"
    :leading-icon="appearance.leadingVariant ? TriangleAlertIcon : undefined"
    :leading-tooltip="appearance.leadingTooltip"
    :to="getTo()"
  >
    <template v-if="transferDelivery" #prefix>
      <ListItemAnnotation
        :to="{ name: 'transferDetail', params: { id: transferDelivery.id } }"
        variant="warning"
      >
        <div class="flex flex-1 items-stretch gap-x-2 font-semibold max-sm:flex-col">
          <div class="flex items-center gap-1">
            <NavigationIcon class="h-4 w-4" />
            {{
              t('deliveryTimes', {
                estimatedDrivingTime: getDurationInHoursAndMinutes(
                  transferDelivery.startDate,
                  transferDelivery.endDate,
                ),
                handOverDate: handOverDate,
              })
            }}
          </div>
          <div>
            {{ t('driver') }}
            {{ transferDelivery.externalDriverEmail ?? transferDelivery.driverName
            }}{{ allocationDriverSuffix }}
          </div>
          <div>
            {{ transferDelivery.comment }}
          </div>
        </div>
      </ListItemAnnotation>
    </template>
    <div class="flex flex-1 items-stretch gap-4">
      <div
        class="flex basis-[15%] flex-col justify-center rounded-sm bg-white/80 p-1 text-center"
        style="color: var(--content-color)"
      >
        <p class="mb-1 font-medium">
          {{ title }}
        </p>
        <div class="text-lg font-semibold">
          {{ characteristics.isReturn && !characteristics.isHandover ? endDate : startDate }}
        </div>
      </div>
      <div class="flex min-w-0 flex-1 gap-1 max-md:flex-col md:gap-4">
        <div class="md:basis-[60%]">
          <div v-if="transaction.type === AllocationType.WORKSHOP_APPOINTMENT">
            <span class="opacity-50">{{ t('workshopAppointment') }}</span>
            <p class="font-semibold">{{ workshopTypesLabel }}</p>
          </div>
          <div v-else-if="!characteristics.isPartnerRent">
            <span class="opacity-50"># </span>{{ transaction.bookingNumber }}
          </div>
          <div v-else>
            <span class="text-obsidian-400">{{ t('ref') }} </span>
            {{ transaction.comment }}
          </div>

          <div
            v-if="
              transaction.type !== AllocationType.WORKSHOP_APPOINTMENT &&
              !characteristics.isPartnerRent
            "
            class="font-semibold"
          >
            {{ transaction.customerName ?? 'n/a' }}
          </div>
          <p v-else-if="characteristics.isPartnerRent">
            {{ t(`rentalPartners.${transaction.rentalPartner}`) }}
          </p>
          <p class="max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
            <LicencePlate>{{ transaction.licencePlate }}</LicencePlate>
            <span class="max-sm:hidden">&nbsp;{{ transaction.carMake }}</span>
            {{ transaction.carModel }}
          </p>
        </div>
        <div
          class="relative flex overflow-hidden rounded-sm bg-white/80 px-3 py-2 md:basis-[40%] xl:w-1/5"
        >
          <div class="my-auto flex min-w-0 flex-col items-start gap-1">
            <p
              v-for="extra in preHandoverExtras"
              :key="extra.type"
              class="flex items-center gap-1 font-semibold"
            >
              <BabyIcon class="size-4 shrink-0" />
              {{ t(`vehicleExtraValues.${extra.type}`, extra.count) }}
            </p>
            <p class="clamp overflow-clip">
              <span v-if="characteristics.isPartnerRent">{{ t('partnerRent') }}</span>
              <span
                v-else-if="
                  transaction.comment ??
                  transaction.bookingComment ??
                  transaction.workshopAppointmentComment
                "
              >
                <p v-if="transaction.comment">{{ transaction.comment }}</p>
                <p v-if="transaction.bookingComment">{{ transaction.bookingComment }}</p>
                <p v-if="transaction.workshopAppointmentComment">
                  {{ transaction.workshopAppointmentComment }}
                </p>
              </span>
              <span v-else-if="!preHandoverExtras.length" class="text-obsidian-400">{{
                t('noComment')
              }}</span>
            </p>
          </div>
        </div>
      </div>

      <DropdownDotted
        :buttons="dropDownItems"
        class="max-sm:absolute max-sm:right-2 max-sm:top-2"
      />
    </div>
    <template v-if="transferReturn" #suffix>
      <ListItemAnnotation
        :to="{ name: 'transferDetail', params: { id: transferReturn.id } }"
        variant="warning"
        position="trailing"
      >
        <div class="flex flex-1 items-stretch gap-x-2 font-semibold max-sm:flex-col">
          <div class="flex items-center gap-1">
            <NavigationIcon class="h-4 w-4" />
            {{
              t('returnTimes', {
                estimatedDrivingTime: getDurationInHoursAndMinutes(
                  transferReturn.startDate,
                  transferReturn.endDate,
                ),
                returnDate,
              })
            }}
          </div>
          <div>{{ t('driver') }} {{ transferReturn.driverName }}</div>
          <div>
            {{ transferReturn.comment }}
          </div>
        </div>
      </ListItemAnnotation>
    </template>
  </ListItem>
</template>

<script setup lang="ts">
import { CarLocationType } from '@/entities/cars/car-location-type.enum';
import { getAllocationTransactionCharacteristics } from '@/utils/get-allocation-transaction-characteristics.ts';
import { formatDate, useFormattedDate } from '@/hooks/use-formatted-date';
import { getDateTimeFormat } from '@/hooks/use-date-format';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import type { DropdownItem } from '@/components/DropdownDotted.vue';
import DropdownDotted from '@/components/DropdownDotted.vue';
import { useRouter } from 'vue-router';
import { CarStatus } from '@/entities/cars/car-status.enum';
import LicencePlate from '@/components/LicencePlate.vue';
import ListItem, { type ListItemVariant } from '@/components/ListItem.vue';
import type { AllocationTransactionDto } from '@/entities/allocations/allocation.entity.ts';
import { AllocationType } from '@carvia/ros-client-types';
import ListItemAnnotation from '@/components/ListItemAnnotation.vue';
import { getDurationInHoursAndMinutes } from '@/utils/duration';
import { BabyIcon, NavigationIcon, TriangleAlertIcon } from 'lucide-vue-next';

const props = defineProps<{
  transaction: AllocationTransactionDto;
}>();

const { t } = useI18n();
const router = useRouter();

const characteristics = computed(() => getAllocationTransactionCharacteristics(props.transaction));

const preHandoverExtras = computed(() =>
  characteristics.value.isHandover ? (props.transaction.preHandoverExtras ?? []) : [],
);

const workshopTypesLabel = computed(() =>
  (props.transaction.workshopAppointmentTypes ?? [])
    .map((type) => t(`workshopAppointmentTypes.${type}`))
    .join(', '),
);

const transferDelivery = computed(() =>
  props.transaction?.transfers?.find((tr) => tr.type === AllocationType.TRANSFER_DELIVERY),
);
const transferReturn = computed(() =>
  props.transaction?.transfers?.find((tr) => tr.type === AllocationType.TRANSFER_RETURN),
);

const allocationDriverSuffix = computed(() => {
  const name = props.transaction.driverName;
  if (!name || name === 'n/a n/a' || name === transferDelivery.value?.driverName) {
    return '';
  }
  return ` & ${name}`;
});

const handOverDate = computed(() => {
  const handoverStart = props.transaction.startDate;
  const transferStart = transferDelivery.value?.startDate;
  if (!handoverStart) {
    return '';
  }
  return formatDate(
    handoverStart,
    transferStart &&
      !DateTime.fromISO(handoverStart).hasSame(DateTime.fromISO(transferStart), 'day')
      ? getDateTimeFormat()
      : DateTime.TIME_24_SIMPLE,
  );
});

const startDate = useFormattedDate(
  () => transferDelivery.value?.startDate,
  DateTime.TIME_24_SIMPLE,
  handOverDate.value,
);

const endDate = useFormattedDate(props.transaction.endDate, DateTime.TIME_24_SIMPLE);

const returnDate = useFormattedDate(
  transferReturn.value?.endDate,
  DateTime.TIME_24_SIMPLE,
  endDate.value,
);

const getTo = () => {
  if (characteristics.value.isSwap && props.transaction.bookingId) {
    return {
      name: characteristics.value.isReturn ? 'swapReturnOverview' : 'swapHandoverStatus',
      params: {
        bookingId: props.transaction.bookingId,
      },
      query: props.transaction.swapFromId
        ? {
            swapFromId: props.transaction.swapFromId,
            swapToId: props.transaction.id,
          }
        : {
            [characteristics.value.isHandover ? 'swapToId' : 'swapFromId']: props.transaction.id,
          },
    };
  } else if (characteristics.value.isWorkshopHandover && props.transaction.workshopAppointmentId) {
    return {
      name: 'workshopAppointmentHandover',
      params: { workshopAppointmentId: props.transaction.workshopAppointmentId },
    };
  } else if (characteristics.value.isWorkshopReturn && props.transaction.workshopAppointmentId) {
    return {
      name: 'workshopAppointmentReturn',
      params: { workshopAppointmentId: props.transaction.workshopAppointmentId },
    };
  } else if (characteristics.value.isHandover && props.transaction.bookingId) {
    return {
      name: 'handoverDetail',
      params: { id: props.transaction.bookingId },
    };
  } else if (characteristics.value.isReturn && props.transaction.bookingId) {
    return {
      name: 'returnOverview',
      params: { id: props.transaction.bookingId },
    };
  } else if (characteristics.value.isPartnerRent) {
    return {
      name: 'partnerRentHandover',
      params: { id: props.transaction.id },
    };
  } else return undefined;
};

const title = computed(() => {
  if (
    characteristics.value.isSwap &&
    characteristics.value.isReturn &&
    characteristics.value.isHandover
  ) {
    return t('swap');
  } else if (characteristics.value.isWorkshopReturn) {
    return t('workshopReturn');
  } else if (characteristics.value.isReturn || characteristics.value.isPartnerRentReturn) {
    return t('return');
  } else if (characteristics.value.isWorkshopHandover) {
    if (characteristics.value.hasTransferDelivery) {
      return t('handoverDeliveryTitle');
    }
    return t('workshopHandover');
  } else if (characteristics.value.isHandover || characteristics.value.isPartnerRentHandover) {
    if (characteristics.value.hasTransferDelivery) {
      return t('handoverDeliveryTitle');
    }
    return t('handover');
  } else return undefined;
});

const dropDownItems = computed<DropdownItem[]>(() => {
  const items = [];
  if (characteristics.value.hasTransferDelivery) {
    items.push({
      onClick: () =>
        router.push({
          name: 'transferEdit',
          params: {
            transferType: transferDelivery.value?.type,
            allocationId: props.transaction.id,
            transferId: transferDelivery.value?.id,
          },
        }),
      title: t('editTransfer'),
    });
  }
  if (characteristics.value.hasTransferReturn) {
    items.push({
      onClick: () =>
        router.push({
          name: 'transferEdit',
          params: {
            transferType: transferReturn.value?.type,
            allocationId: props.transaction.id,
            transferId: transferReturn.value?.id,
          },
        }),
      title: t('editTransfer'),
    });
  }
  if (characteristics.value.isPartnerRent) {
    items.push({
      onClick: () =>
        router.push({
          name: 'partnerRentEdit',
          params: {
            carId: props.transaction.carId,
            allocationId: props.transaction.id,
          },
        }),
      title: t('editPartnerRent'),
    });
  }
  if (
    (characteristics.value.isHandover || characteristics.value.isReturn) &&
    props.transaction.bookingId
  ) {
    items.push({
      onClick: () =>
        router.push({ name: 'bookingEdit', params: { id: props.transaction.bookingId } }),
      title: t('editBooking'),
    });
    items.push({
      onClick: () =>
        router.push({
          name: 'editCarStatus',
          params: { id: props.transaction.carId },
        }),
      title: t('editCarStatus'),
    });
  }
  if (characteristics.value.isWorkshopHandover || characteristics.value.isWorkshopReturn) {
    if (props.transaction.workshopAppointmentId) {
      items.push({
        onClick: () =>
          router.push({
            name: 'editWorkshopAppointment',
            params: { id: props.transaction.workshopAppointmentId! },
          }),
        title: t('editWorkshopAppointment'),
      });
    }
    items.push({
      onClick: () =>
        router.push({
          name: 'editCarStatus',
          params: { id: props.transaction.carId },
        }),
      title: t('editCarStatus'),
    });
  }
  return items;
});

function getListItemAppearance(transaction: AllocationTransactionDto): {
  variant: ListItemVariant;
  leadingVariant?: ListItemVariant;
  leadingTooltip?: string;
} {
  const characteristics = getAllocationTransactionCharacteristics(transaction);
  if (characteristics.isSwap && characteristics.isHandover && characteristics.isReturn) {
    return {
      variant: 'transition',
      leadingVariant: getHandoverIssue(transaction, characteristics),
      leadingTooltip: getHandoverIssueTooltip(transaction, characteristics),
    };
  }
  if (characteristics.isPartnerRent) {
    return { variant: 'orange' };
  }
  if (characteristics.isWorkshopReturn || characteristics.isWorkshopHandover) {
    return {
      variant: 'orange',
      leadingVariant: transaction.isReadyForPickup ? 'danger' : undefined,
      leadingTooltip: transaction.isReadyForPickup ? t('readyForPickup') : undefined,
    };
  }
  if (characteristics.isReturn) {
    return {
      variant: 'info',
      leadingVariant: characteristics.isLateReturn ? 'danger' : undefined,
      leadingTooltip: characteristics.isLateReturn ? t('lateReturn') : undefined,
    };
  }
  if (characteristics.isHandover) {
    return {
      variant: 'success',
      leadingVariant: getHandoverIssue(transaction, characteristics),
      leadingTooltip: getHandoverIssueTooltip(transaction, characteristics),
    };
  }
  return { variant: 'default' };
}

function getHandoverIssue(
  transaction: AllocationTransactionDto,
  characteristics: ReturnType<typeof getAllocationTransactionCharacteristics>,
): ListItemVariant | undefined {
  if (
    characteristics.isLateHandover ||
    transaction.carCurrentLocationType !== CarLocationType.STATION ||
    transaction.carStatus === CarStatus.ORDERED
  ) {
    return 'danger';
  }
  if (!transaction.carReadyToHandover) {
    return 'problem';
  }
}

function getHandoverIssueTooltip(
  transaction: AllocationTransactionDto,
  characteristics: ReturnType<typeof getAllocationTransactionCharacteristics>,
) {
  const reasons = [
    ...(characteristics.isLateHandover ? [t('lateHandover')] : []),
    ...(transaction.carCurrentLocationType !== CarLocationType.STATION
      ? [t('carNotAtStation')]
      : []),
    ...(transaction.carStatus === CarStatus.ORDERED ? [t('carOrdered')] : []),
    ...(!transaction.carReadyToHandover ? [t('notReadyToHandover')] : []),
  ];
  return reasons.length ? reasons.join('\n') : undefined;
}

const appearance = computed(() => getListItemAppearance(props.transaction));
</script>

<style scoped>
.clamp {
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
</style>

<i18n lang="json">
{
  "en": {
    "handover": "Handover",
    "return": "Return",
    "vehicle": "Vehicle",
    "customer": "Customer",
    "driver": "Driver",
    "licencePlate": "Lp.",
    "bookingNumber": "Bnr.",
    "markAsHandoverReady": "Mark as handover ready",
    "markAsOpen": "Mark as open",
    "editBooking": "edit booking",
    "editTransfer": "edit transfer",
    "editPartnerRent": "edit partner rent",
    "editCarStatus": "edit car status",
    "addTransfer": "add transfer",
    "rentalPartner": "Rental Partner",
    "partnerRent": "Partner Rent",
    "ref": "Ref.",
    "noComment": "No Comment",
    "swap": "Swap",
    "handoverDeliveryTitle": "Departure",
    "deliveryTimes": "{estimatedDrivingTime} drive (handover {handOverDate})",
    "returnTimes": "{estimatedDrivingTime} drive (return {returnDate})",
    "workshopAppointment": "Workshop appointment",
    "workshopHandover": "Workshop handover",
    "workshopReturn": "Workshop return",
    "editWorkshopAppointment": "Edit workshop appointment",
    "lateHandover": "Handover is overdue",
    "lateReturn": "Return is overdue",
    "carNotAtStation": "Vehicle was not returned to the station",
    "carOrdered": "Vehicle is ordered",
    "notReadyToHandover": "Vehicle is not ready for handover",
    "readyForPickup": "Vehicle is ready for pickup"
  },
  "de": {
    "handover": "Übergabe",
    "return": "Rückgabe",
    "vehicle": "Fahrzeug",
    "customer": "Kunde",
    "driver": "Fahrer",
    "licencePlate": "Knz.",
    "bookingNumber": "Bnr.",
    "markAsHandoverReady": "Als Übergabebereit markieren",
    "markAsOpen": "Als Offen markieren",
    "editBooking": "Buchung bearbeiten",
    "editTransfer": "Transfer bearbeiten",
    "editPartnerRent": "Partnermiete bearbeiten",
    "editCarStatus": "Fahrzeugstatus bearbeiten",
    "addTransfer": "Transfer hinzufügen",
    "rentalPartner": "Mietpartner",
    "partnerRent": "Partnermiete",
    "ref": "Ref.",
    "noComment": "Kein Kommentar",
    "swap": "Tausch",
    "handoverDeliveryTitle": "Aufbruch",
    "deliveryTimes": "{estimatedDrivingTime} Fahrt (Übergabe {handOverDate})",
    "returnTimes": "{estimatedDrivingTime} Fahrt (Rückkehr {returnDate})",
    "workshopAppointment": "Werkstatttermin",
    "workshopHandover": "Werkstatt-Übergabe",
    "workshopReturn": "Werkstatt-Rückgabe",
    "editWorkshopAppointment": "Werkstatttermin bearbeiten",
    "lateHandover": "Übergabe ist überfällig",
    "lateReturn": "Rückgabe ist überfällig",
    "carNotAtStation": "Fahrzeug wurde noch nicht zurückgenommen",
    "carOrdered": "Fahrzeug ist bestellt",
    "notReadyToHandover": "Fahrzeug ist nicht übergabebereit",
    "readyForPickup": "Fahrzeug ist abholbereit"
  },
  "es": {
    "handover": "Entrega",
    "return": "Devolución",
    "vehicle": "Vehículo",
    "customer": "Cliente",
    "driver": "Conductor",
    "licencePlate": "Matr.",
    "bookingNumber": "N.º res.",
    "markAsHandoverReady": "Marcar listo para entrega",
    "markAsOpen": "Marcar como abierto",
    "editBooking": "editar reserva",
    "editTransfer": "editar traslado",
    "editPartnerRent": "editar alquiler partner",
    "editCarStatus": "editar estado del vehículo",
    "addTransfer": "añadir traslado",
    "rentalPartner": "Partner de alquiler",
    "partnerRent": "Alquiler partner",
    "ref": "Ref.",
    "noComment": "Sin comentario",
    "swap": "Cambio",
    "handoverDeliveryTitle": "Salida",
    "deliveryTimes": "{estimatedDrivingTime} de trayecto (entrega {handOverDate})",
    "returnTimes": "{estimatedDrivingTime} de trayecto (devolución {returnDate})",
    "workshopAppointment": "Cita de taller",
    "workshopHandover": "Entrega en taller",
    "workshopReturn": "Devolución en taller",
    "editWorkshopAppointment": "Editar cita de taller",
    "lateHandover": "La entrega está atrasada",
    "lateReturn": "La devolución está atrasada",
    "carNotAtStation": "El vehículo no fue devuelto a la estación",
    "carOrdered": "El vehículo está pedido",
    "notReadyToHandover": "El vehículo no está listo para la entrega",
    "readyForPickup": "El vehículo está listo para recogida"
  }
}
</i18n>
